// ===== High Tide — hero boot diagnostic (read-only) =====
// Inert unless the URL has ?htdebug=1 or #htdebug. The hash form matters
// because Shopify consumes ?preview_theme_id server-side and 302s to a clean
// URL, stripping other query params with it.
//
// This exists to answer one question during the "first few swipes do nothing"
// window on a cold load: are the touches not ARRIVING (main thread blocked), or
// are they arriving and the page refusing to MOVE (a scroll lock)? Those have
// completely different causes and we have been guessing between them.
//
// Rules, because a previous version of this file broke the page:
//   * pointer-events:none — it must never swallow a touch.
//   * no scroll container of its own.
//   * all listeners passive; all layout reads inside one 200ms timer.
(() => {
  try {
    const on = /[?&]htdebug=1/.test(location.search) || /(^|#)htdebug\b/.test(location.hash);
    if (!on) return;
  } catch (e) { return; }

  const T0 = performance.now();
  const ms = (t) => (t == null ? '—' : Math.round(t) + 'ms');
  const st_ = (window.Shopify && window.Shopify.theme) || {};
  const theme = { name: String(st_.name || '?').slice(0, 22), role: st_.role || '?' };

  const log = {
    readyStateAtBoot: document.readyState,
    heroReady: null, htAppeared: null,
    touchStarts: 0, touchMoves: 0,
    scrollEvents: 0,
    movedWithoutScroll: 0,   // touchmove fired but scrollY never changed
    ctxLost: 0,
    maxStallMs: 0,           // longest gap between animation frames
    totalStallMs: 0,         // cumulative time lost to gaps > 100ms
    errors: [],
  };

  window.addEventListener('error', (e) => {
    log.errors.push(String(e.message || 'err').slice(0, 60) + '@' + String(e.filename || '').split('/').pop());
  });
  window.addEventListener('unhandledrejection', (e) => {
    log.errors.push('rej:' + String((e.reason && e.reason.message) || e.reason).slice(0, 60));
  });
  document.addEventListener('hightide:hero-ready', () => { log.heroReady = performance.now(); }, { once: true });

  // --- touch vs scroll: the decisive pair -------------------------------
  let scrollAtTouchStart = 0;
  let sawScrollSinceTouch = false;
  window.addEventListener('touchstart', () => {
    log.touchStarts++;
    scrollAtTouchStart = window.scrollY;
    sawScrollSinceTouch = false;
  }, { passive: true });
  window.addEventListener('touchmove', () => {
    log.touchMoves++;
    if (!sawScrollSinceTouch && window.scrollY === scrollAtTouchStart) log.movedWithoutScroll++;
  }, { passive: true });
  window.addEventListener('scroll', () => {
    log.scrollEvents++;
    sawScrollSinceTouch = true;
  }, { passive: true });

  // --- main-thread stall detector ---------------------------------------
  // A gap between animation frames is time the main thread was blocked. This is
  // the single most useful number here: if the stuck window lines up with a
  // large stall, the cause is blocking work, not a scroll lock.
  let lastFrameAt = performance.now();
  let renderFrames = 0, lastRenderFrame = -1;
  const frame = () => {
    const now = performance.now();
    const gap = now - lastFrameAt;
    lastFrameAt = now;
    if (gap > 100) { log.totalStallMs += gap; if (gap > log.maxStallMs) log.maxStallMs = gap; }
    const ht = window.__ht;
    if (ht && ht.renderer && ht.renderer.info) {
      const f = ht.renderer.info.render.frame;
      if (f !== lastRenderFrame) { renderFrames++; lastRenderFrame = f; }
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  const box = document.createElement('div');
  box.setAttribute('style', [
    'position:fixed', 'left:0', 'right:0', 'top:0', 'z-index:2147483647',
    'font:10px/1.3 ui-monospace,Menlo,monospace', 'background:rgba(0,0,0,.82)',
    'color:#7CFFB2', 'padding:4px 6px', 'white-space:pre', 'pointer-events:none',
  ].join(';'));
  const attach = () => (document.body || document.documentElement).appendChild(box);
  if (document.body) attach(); else document.addEventListener('DOMContentLoaded', attach, { once: true });

  let windowStart = performance.now();
  setInterval(() => {
    const now = performance.now();
    const ht = window.__ht;
    if (ht && log.htAppeared == null) log.htAppeared = now;
    if (!log.ctxLost) {
      const c = document.querySelector('#canStage canvas');
      if (c && c.__htHooked !== true) {
        c.__htHooked = true;
        c.addEventListener('webglcontextlost', () => { log.ctxLost++; }, false);
      }
    }
    const fps = Math.round((renderFrames * 1000) / (now - windowStart));
    renderFrames = 0; windowStart = now;

    const hs = getComputedStyle(document.documentElement);
    const bs = getComputedStyle(document.body);

    box.textContent = [
      theme.name + '  role=' + theme.role,
      'boot      ' + log.readyStateAtBoot + '  __ht=' + (ht ? ms(log.htAppeared - T0) : 'ABSENT') +
        '  ready=' + (log.heroReady ? ms(log.heroReady - T0) : 'no'),
      'phase     ' + (ht ? ht.phase : '—') + '  render/s=' + fps + '  ctxLost=' + log.ctxLost,
      'TOUCH     starts=' + log.touchStarts + ' moves=' + log.touchMoves + ' scrollEvts=' + log.scrollEvents,
      'DEADMOVES ' + log.movedWithoutScroll + '   <-- moves that did NOT scroll the page',
      'STALL     max=' + Math.round(log.maxStallMs) + 'ms total=' + Math.round(log.totalStallMs) + 'ms',
      'scrollY   ' + Math.round(window.scrollY) + '  docH=' + document.documentElement.scrollHeight,
      'html      of=' + hs.overflow + '/' + hs.overflowY + ' ta=' + hs.touchAction,
      'body      of=' + bs.overflow + '/' + bs.overflowY + ' ta=' + bs.touchAction,
      'bodyClass ' + String(document.body.className).slice(0, 60),
      'err       ' + (log.errors.length ? log.errors.slice(-2).join(' | ') : 'none'),
    ].join('\n');
  }, 200);
})();
